import React, { ReactNode } from 'react';
import { KeyValuePair } from 'data/types';
import { CaseModel } from 'data/case/case.types';
import {
  TargetModel, TargetModelAccessFunction, TargetModelAfCCCSettings, TargetModelMap,
} from 'data/target/target.types';
import { connect } from 'react-redux';
import { Team } from 'data/team/team.types';
import { Warrant } from 'data/warrant/warrant.types';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { FormTemplateSection, Template } from 'data/template/template.types';
import { getFirstTargetTag } from 'xcipio/devices/forms/combineTargetsWithSameTypeAndValue';
import { DeviceModelMap } from 'xcipio/devices/device.types';
import { localizeLabel } from 'shared/form/form.i18n';
import { Contact } from 'data/contact/contact.types';
import { dateTimeFormatter } from '../../../shared/constants';
import { FormPanel } from '../../../shared/wizard/with-discovery-wizard-step-panel';
import './warrant-wizard.scss';
import { DevicePanel } from '../../devices';
import ChangeTracker from '../forms/warrant-change-tracker';

interface SummaryProps {
  editMode: boolean; // true if show original value
  changeTracker: ChangeTracker | null;
  templateUsed?: Template | null;
  warrant?: any;
  cases?: any;
  derivedTargets?:any;
  warrantConfigInstance?:any;
  contacts?: Contact[];
  teams?: Team[];
  targetsById: TargetModelMap;
  targetValidationOpen: boolean;
  handleDevicesToConfirm: (devices: DeviceModelMap) => void;
}

interface Field {
  group: string;
  field: string;
  label: any;
  value: any;
  original: any;
}

interface FieldMap {
  name: { label: string, value: string },
  template: { label: string, value: string },
  receivedDateTime: { label: string, value: string },
  startDateTime: { label: string, value: string },
  stopDateTime: { label: string, value: string },
  timeZone: { label: string, value: string },
  caseType: { label: string, value: string },
  lea: { label: string, value: string },
  caseOptTemplateUsed: { label: string, value: string },
  contact: { label: string, value: string },
  visibility: { label: string, value: string },
  attachments: { label: string, value: string[] }
}

export class Summary extends React.Component<SummaryProps, any> implements FormPanel {
  warrantFields:Field[] = [];

  serviceOptions:{label:string, value:string}[] = [];

  devices: any;

  cases:any = {};

  afcccMap: any = {};

  includedAfMap: any = {};

  includedAfAutoTypeMap: any = {};

  includedAfAutoGroupMap: any = {};

  includedAfGroupMap: any = {};

  guiListId = 0;

  constructor(props:SummaryProps) {
    super(props);
    this.initializeData();
    this.serviceOptions = this.props.warrantConfigInstance.getServiceDropDownOptions();
    this.state = {
      valid: true,
    };
  }

  isNewCase = (aCase:any) => (this.props.changeTracker?.isNewCase(aCase.id) || !this.props.editMode);

  static getContact = (contacts?: Contact[], warrant?:Warrant) => {
    if (!contacts || !warrant) {
      return null;
    }

    const contactFound = contacts.find((contact) => contact.id === warrant.contact?.id);
    if (contactFound) {
      return contactFound.name;
    }

    return '';
  }

  static getVisibility = (teams?: Team[], warrant?:Warrant) => {
    if (!teams || !warrant) {
      return null;
    }

    const teamFound = teams.find((team) => team.id === warrant.dxOwner);

    if (teamFound) {
      return teamFound.name;
    }

    return '';
  }

  initializeData = () => {
    // warrant
    this.warrantFields.push({
      group: `warrant.${this.props.changeTracker?.getWarrantId()}`,
      field: 'name',
      label: i18n.t('warrant.summary.warrantName'),
      value: this.props.warrant.name,
      original: null,
    });
    this.warrantFields.push({
      group: `warrant.${this.props.changeTracker?.getWarrantId()}`,
      field: 'template',
      label: i18n.t('warrant.summary.template'),
      value: this.props.templateUsed?.name,
      original: null,
    });
    this.warrantFields.push({
      group: `warrant.${this.props.changeTracker?.getWarrantId()}`,
      field: 'contact',
      label: i18n.t('warrant.summary.contact'),
      value: Summary.getContact(this.props.contacts, this.props.warrant),
      original: null,
    });
    this.warrantFields.push({
      group: `warrant.${this.props.changeTracker?.getWarrantId()}`,
      field: 'visibility',
      label: i18n.t('warrant.summary.visibility'),
      value: Summary.getVisibility(this.props.teams, this.props.warrant),
      original: null,
    });

    const warrantCaseOptionFields:any[] = [];

    // Create a data structure to be used for displaying the summary.
    this.props.cases.forEach((aCase:any) => {
      // this.cases is a data structure to keep track of the case related fields for display.
      this.cases[aCase.id] = {
        // eslint-disable-next-line max-len
        id: aCase.id, case: aCase, liIdField: null, hi3InterfaceFields: null, fields: [],
      };

      // for warrant creation, collect the list of AF Group, AF Type and AF Autowire Group
      // from the case itself.
      if (this.props.editMode === false) {
        aCase.targets.forEach((target:any) => {
          target.afs.afIds.forEach((af:any) => {
            this.addAfLabel(aCase.id, target.id, af.name, this.includedAfMap);
          });

          target.afs.afGroups.forEach((afGroup:any) => {
            this.addAfLabel(aCase.id, target.id, afGroup.name, this.includedAfGroupMap);
          });

          target.afs.afAutowireTypes.forEach((afAutowireType:any) => {
            this.addAfLabel(aCase.id, target.id, afAutowireType.name, this.includedAfAutoTypeMap);
          });

          target.afs.afAutowireGroups.forEach((afAutowireGroup:any) => {
            this.addAfLabel(aCase.id, target.id, afAutowireGroup.name, this.includedAfAutoGroupMap);
          });
        });
      }
    });

    const fieldSummary = this.props.changeTracker?.getSummary();

    fieldSummary.forEach((field: any) => {
      // warrant
      if (field.group.startsWith('warrant.')) {
        field.label = localizeLabel(field.field, field.label, 'warrant.form');

        if (field.field === 'name') {
          this.warrantFields.shift(); // remove the first item that is the case name.
          this.warrantFields.unshift(field);
        } else if (field.field.startsWith('caseOptTemplateUsed.')) {
          // display case option template name before the option parameters.
          warrantCaseOptionFields.unshift(field);
        } else {
          this.warrantFields.push(field);
        }

      // warrant case options
      } else if (field.group.startsWith('case.undefined') && field.field.startsWith('caseOption.')) {
        field.label = localizeLabel(field.field, field.label, 'warrant.caseOption.form');
        warrantCaseOptionFields.push(field);

      // afccc
      } else if (field.group.startsWith('case.') && field.field.startsWith('af.') && field.field.indexOf('.afccc.') > -1) {
        field.label = localizeLabel(field.field, field.label, 'warrant.case.form');
        this.addAfCccField(field);

      // cases
      } else if (field.group.startsWith('case.') && !(field.field.startsWith('af.') || field.field.startsWith('afGrp.') || field.field.startsWith('afAutoType.') || field.field.startsWith('afAutoGrp.'))) {
        field.label = localizeLabel(field.field, field.label, 'warrant.case.form');
        this.addCaseField(field);
      }
    });
    if (this.props.editMode === false) { // create mode
      const { attachments } = this.props.warrant;
      if (attachments && attachments.length > 0) {
        const field = {
          group: 'warrant.NEWID+0',
          field: 'attachments',
          original: '',
          value: [],
          action: '',
          label: i18n.t('warrant.summary.attachments'),
        };
        field.value = attachments.map((attachment:any) => attachment.name);
        this.warrantFields.push(field);
      }
    }
    this.warrantFields = this.warrantFields.concat(warrantCaseOptionFields);

    this.devices = DevicePanel.getDevices(
      this.props.warrant,
      this.props.cases,
      this.props.derivedTargets,
      this.props.targetsById,
    );

    this.props.handleDevicesToConfirm(this.devices);
  }

  addCaseField = (changeTrackerField: any) => {
    const parts:string[] = changeTrackerField.group.split('.');

    // no case in redux matches the case in changeTracker
    const aCase = this.cases[parts[1]];
    if (!aCase) return;

    // do not display field includeInWarrant
    if (changeTrackerField.field === 'includeInWarrant') return;

    // Case name was modified, use the changeTrackerField
    if (changeTrackerField.field === 'liId') {
      aCase.fields.shift(); // remove the first item that is the case name.
      aCase.fields.unshift(changeTrackerField); // add the case name change record
      return;
    }

    // for warrant edit
    if (this.props.editMode) {
      if (changeTrackerField.field === 'liId') {
        aCase.liIdField = changeTrackerField;
      } else {
        aCase.fields.push(changeTrackerField);
      }

    // for warrant creation:
    } else if (changeTrackerField.field.startsWith('HI3Interfaces.')) {
      // display all HI3 interfaces if there is a modification
      if (aCase.hi3InterfaceFields === null
        && aCase.case.hi3Interfaces
        && aCase.case.hi3Interfaces.length > 0) {
        aCase.hi3InterfaceFields = {
          group: `case.${parts[1]}`,
          field: 'HI3Interfaces',
          label: i18n.t('warrant.summary.communicationContent', { count: aCase.case?.hi3Interfaces?.length ?? 1 }),
          original: null,
          value: aCase.case.hi3Interfaces.map(
            (hi3Interface:any) => (hi3Interface.name),
          ),
        };
        aCase.bAddedHI3InterfaceField = true;
      }
    } else if (changeTrackerField.field === 'target'
      || changeTrackerField.field === 'cfId'
      || changeTrackerField.field === 'services') {
      // ignore because target, CF, services is already added in initializeData
    } else {
      aCase.fields.push(changeTrackerField);
    }
  }

  addAfCccField = (field:any) => {
    const parts: string[] = field.group.split('.');
    let targets = this.afcccMap[parts[1]];
    if (!targets) {
      targets = {};
      this.afcccMap[parts[1]] = targets;
    }
    let afMap = targets[parts[3]];
    if (!afMap) {
      afMap = {};
      targets[parts[3]] = afMap;
    }
    const fieldParts: string[] = field.field.split('.');
    let afcccList: any[] = afMap[fieldParts[1]];
    if (!afcccList) {
      afcccList = [];
      afMap[fieldParts[1]] = afcccList;
    }
    afcccList.push(field);
  }

  addAfLabel = (caseId:string, targetId: string, fieldLabel:string, excludeMap: any) => {
    let targets = excludeMap[caseId];
    if (!targets) {
      targets = {};
      excludeMap[caseId] = targets;
    }
    let excludedItems = targets[targetId];
    if (!excludedItems) {
      excludedItems = [];
      targets[targetId] = excludedItems;
    }
    excludedItems.push(fieldLabel);
  }

  isValid = (errors:string[]) => {
    return this.state.valid === true;
  }

  submit = async () => {
    return true;
  }

  isFieldVisibleInTemplate(fieldName: string): boolean {
    const {
      templateUsed,
    } = this.props;

    if (templateUsed == null) {
      return false;
    }

    const formTemplateSection = templateUsed.template.general as FormTemplateSection;
    const field = formTemplateSection?.fields?.[fieldName];
    if (field == null) {
      return false;
    }

    return (field.hide ?? 'false') !== 'true';
  }

  toTargetLabel = (targetName: string) => {
    targetName = getFirstTargetTag(targetName) ?? targetName;
    return this.props.warrantConfigInstance.getConfiguration().config.targets[targetName]?.title;
  };

  toServiceLabel = (serviceValue: string) => {
    const findItem: { label: string; value: string } | undefined = this.serviceOptions.find(
      (item: { label: string; value: string }) => item.value === serviceValue,
    );
    if (findItem) {
      return findItem.label;
    }
    return serviceValue;
  };

  static toDateString(value: string | Date): string {
    if (value == null || value === '') {
      return '';
    }

    if (typeof value === 'string') {
      value = new Date(value);
    }

    return dateTimeFormatter(value);
  }

  toString = (value:any) => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) {
      return dateTimeFormatter(value);
    }

    return value.toString();
  }

  nextId = () => { this.guiListId += 1; return this.guiListId; }

  renderTargetCCC = (afccc: any, afKey: any, target: any, aCase: any, i: number) => {
    let afName = '';
    let targetInfoOptions;
    if (target?.targetInfoHash?.length > 0 && target.targetInfoHash[0].targetInfos?.length > 0) {
      targetInfoOptions = target.targetInfoHash[0].targetInfos[i]?.options ?? [];
    } else {
      targetInfoOptions = [];
    }

    const afCCCOptions = afccc?.option ?? [];
    const targetCCCInfo = [...targetInfoOptions, ...afCCCOptions];
    if (aCase.afMapping.GENBANDC20) {
      afName = target.afs.afIds.filter((afid : TargetModelAccessFunction) => afid.id === afKey)[0].name;
    }

    if (aCase.afMapping.GENBANDC20) {
      return (
        <React.Fragment key={this.nextId()}>
          <div key={this.nextId()}>
            <div style={{ textAlign: 'left' }}>{i18n.t('warrant.summary.targetInfoFor', { name: afName })}</div>
            {targetCCCInfo?.map((info) => {
              return (
                <li key={this.nextId()}>
                  {this.renderSummaryField(info.key, info.value)}
                </li>
              );
            })}
          </div>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={this.nextId()}>
        {i === 0 ? (
          <div key={this.nextId()}>
            <div style={{ textAlign: 'left' }}>{i18n.t('warrant.summary.targetInfoFor', { name: this.afcccMap[aCase.id][target.id][afKey][0].label.afLabel })}</div>
          </div>
        ) : null}
        <li key={this.nextId()}>
          {this.renderSummaryField(afccc.label.fieldLabel || afccc.label, afccc.value)}
        </li>
      </React.Fragment>
    );
  };

  renderTargetCCCList = (aCase: any, target: any) => {
    let afKeys = [];
    if (Object.keys(this.afcccMap).length) {
      afKeys = Object.keys(this.afcccMap[aCase.id][target.id]);
    } else {
      afKeys = target.afs.afIds.map((id : TargetModelAccessFunction) => id.id);
    }

    if (aCase.afMapping.GENBANDC20) {
      return afKeys.map((afKey: string, i:number) => {
        const thisAfccc = target?.afs?.afccc[0]?.afSettings.filter((afSetting : TargetModelAfCCCSettings) => {
          return afSetting.afids[0] === afKey;
        });
        let targetAccValue;
        // no render for target CCC if there is no targetCCC
        if (!thisAfccc || thisAfccc.length <= 0) {
          return;
        }
        return this.renderTargetCCC(thisAfccc[0], afKey, target, aCase, i);
      });
    }

    if (afKeys && afKeys.length > 0) {
      return (
        <React.Fragment key={this.nextId()}>
          {afKeys.map((afKey: any, i:any) =>
            (this.afcccMap?.[aCase.id]?.[target.id]?.[afKey]?.length > 0
              // eslint-disable-next-line no-shadow
              ? this.afcccMap?.[aCase.id]?.[target.id]?.[afKey]?.map((afccc: any, i: number) =>
                this.renderTargetCCC(afccc, afKey, target, aCase, i))
              : null))}
        </React.Fragment>
      );
    }
    return null;
  };

  limitStringLength = (str:string, maxLength:number) => {
    if (!str) return str;
    if (str.length > maxLength) {
      return `${str.substring(0, maxLength - 3)}...`;
    }
    return str;
  }

  renderItemMap = (itemMap: any, aCase: any, target: any, label: string) => {
    return aCase && itemMap[aCase.id] && itemMap[aCase.id][target.id]
    && itemMap[aCase.id][target.id].length > 0 ? (
      <tr key={this.nextId()}>
        <th style={{ textAlign: 'left' }}>{label}:</th>
        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
          {this.limitStringLength(itemMap[aCase.id][target.id].join(', '), 1000)}
        </td>
      </tr>
      ) : null;
  };

  renderSummaryItemMap = (itemMap: any, aCase: any, target: any, label: string) => {
    return aCase && itemMap[aCase.id] && itemMap[aCase.id][target.id]
    && itemMap[aCase.id][target.id].length > 0 ? (
      <li key={this.nextId()}>
        {this.renderSummaryField(label, this.limitStringLength(itemMap[aCase.id][target.id].join(', '), 1000))}
      </li>
      ) : null;
  };

  hasAfOrCCC = (aCase: any, target: any) =>
    aCase &&
    ((this.afcccMap[aCase.id] &&
      this.afcccMap[aCase.id][target.id] &&
      Object.keys(this.afcccMap[aCase.id][target.id]).length > 0) ||
      (aCase &&
        this.includedAfMap[aCase.id] &&
        this.includedAfMap[aCase.id][target.id] &&
        this.includedAfMap[aCase.id][target.id].length > 0) ||
      (aCase &&
        this.includedAfGroupMap[aCase.id] &&
        this.includedAfGroupMap[aCase.id][target.id] &&
        this.includedAfGroupMap[aCase.id][target.id].length > 0) ||
      (aCase &&
        this.includedAfAutoTypeMap[aCase.id] &&
        this.includedAfAutoTypeMap[aCase.id][target.id] &&
        this.includedAfAutoTypeMap[aCase.id][target.id].length > 0) ||
      (aCase &&
        this.includedAfAutoGroupMap[aCase.id] &&
        this.includedAfAutoGroupMap[aCase.id][target.id] &&
        this.includedAfAutoGroupMap[aCase.id][target.id].length > 0));

  renderSummaryField = (label:string, value: number | string) => {
    return (
      <div className="summary-card-field">
        <strong className="summary-card-field-label">{label}: </strong>
        <span className="summary-card-field-value">{(label === 'CF' || label === 'Intercept Type')
          && this.props.targetValidationOpen ? '**************' : this.toString(value)}
        </span>
      </div>
    );
  }

  static shouldRenderSecondaryTypeValue(target: TargetModel): boolean {
    if (target.primaryType === 'VZID') {
      return false;
    }

    return target.secondaryValue != null && target.secondaryValue !== '';
  }

  renderSummaryFieldEllipsis(label: string, value: string): ReactNode {
    return (
      <div className="summary-card-field summary-card-field-ellipsis">
        <strong className="summary-card-field-label">{label}: </strong>
        <span className="summary-card-field-value" title={value}>{this.props.targetValidationOpen ? '***************' : this.toString(value)}</span>
      </div>
    );
  }

  renderTargets = (target: any, aCase: any) => {
    return (
      <React.Fragment key={this.nextId()}>
        {target.primaryValue ?
          this.renderSummaryFieldEllipsis(`${this.toTargetLabel(target.primaryType)}`, target.primaryValue) : null}
        {Summary.shouldRenderSecondaryTypeValue(target) ?
          this.renderSummaryFieldEllipsis(`${this.toTargetLabel(target.secondaryType)}`, target.secondaryValue) : null}
        {(target.primaryValue || target.secondaryValue) && this.hasAfOrCCC(aCase, target) ? (
          <ul className="summary-card-target-sub-items" key={this.nextId()}>
            {this.renderSummaryItemMap(this.includedAfMap, aCase, target, i18n.t('warrant.summary.targetAfs'))}
            {this.renderSummaryItemMap(this.includedAfGroupMap, aCase, target, i18n.t('warrant.summary.targetAfGroups'))}
            {this.renderSummaryItemMap(this.includedAfAutoTypeMap, aCase, target, i18n.t('warrant.summary.targetAfAutowireTypes'))}
            {this.renderSummaryItemMap(this.includedAfAutoGroupMap, aCase, target, i18n.t('warrant.summary.targetAfAutowireGroups'))}
            {aCase && ((this.afcccMap[aCase.id] && this.afcccMap[aCase.id][target.id]) || aCase.afMapping.GENBANDC20)
              ? this.renderTargetCCCList(aCase, target)
              : null}
          </ul>
        ) : null}
      </React.Fragment>
    );
  };

  renderDeviceItem = (aDevice:any) => {
    const targetTypes:any[] = Object.keys(aDevice.types);
    return (
      <div className="summary-card">
        <div className="summary-card-info">
          {this.renderSummaryField(i18n.t('warrant.summary.device'), this.toString(aDevice.device))}
          {this.renderSummaryField(i18n.t('warrant.summary.service'), aDevice.services.map((service: string) => this.toServiceLabel(service)).join(', '))}
          { (targetTypes && targetTypes.length > 0) ?
            targetTypes.map((type:any) =>
              aDevice.types[type]?.map((element:any) =>
                (element.display ? (this.renderTargets(element.target, null))
                  : null))) : null}
        </div>
      </div>
    );
  }

  renderCaseCF(cfId: {name: string, id: string}) {
    if (!cfId) {
      return null;
    }

    const cfName = cfId.name || cfId.id;

    return this.renderSummaryField(i18n.t('warrant.summary.collectionFunction'), cfName);
  }

  renderHi3InterfaceFields(hi3InterfaceFields: Field): JSX.Element | null {
    if (!hi3InterfaceFields || !hi3InterfaceFields.label) {
      return null;
    }

    return this.renderSummaryField(hi3InterfaceFields.label, hi3InterfaceFields.value);
  }

  renderCaseField = (field: Field) => {
    if (!field.label) return null;

    const aFieldLabel = field.label.fieldLabel || field.label;
    const { value } = field;

    return this.renderSummaryField(aFieldLabel, value);

  }

  renderCaseFields(fields: Field[]) {

    if (!fields?.length) {
      return null;
    }

    return fields.map(this.renderCaseField);
  }

  renderCaseTargets= (caseModel: CaseModel) => {
    if (!caseModel?.targets || !caseModel?.targets?.length) {
      return null;
    }

    return caseModel.targets.map((target: TargetModel) => this.renderTargets(target, caseModel));
  }

  renderNewCase(aCase:any) {
    return (
      <div key={this.nextId()} className="summary-card summary-card-case">
        {this.renderSummaryField(i18n.t('warrant.summary.name'), this.toString(aCase.case.liId))}
        {this.renderSummaryField(i18n.t('warrant.summary.service'), aCase.case.services.map((service: string) => this.toServiceLabel(service)).join(', '))}
        <div>
          {this.renderCaseCF(aCase.case.cfId)}
          {this.renderHi3InterfaceFields(aCase.hi3InterfaceFields)}
          {this.renderCaseFields(aCase.fields)}
          {this.renderCaseTargets(aCase.case)}
        </div>
      </div>
    );
  }

  renderField = (field: {label:any, value:any, original:any},
    index:number) => {
    if (!field.label) return null;
    let aFieldLabel = field.label;
    if (field.label.fieldLabel) aFieldLabel = field.label.fieldLabel;
    return (
      <tr key={this.nextId()}>
        <th className={index === 0 ? 'summary-table-first-row' : undefined}>{aFieldLabel}:</th>
        <td className={index === 0 ? 'summary-table-first-row' : undefined}>{this.toString(field.value)}</td>
        { (this.props.editMode) ?
          <td className={index === 0 ? 'summary-table-first-row' : undefined} style={{ color: 'lightSlateGray', textDecoration: 'line-through', textAlign: 'left' }}> {this.toString(field.original)}</td> : null }
      </tr>
    );
  }

  static getWarrantFieldsMap = (warrantFields:Field[]):FieldMap => {
    const initialValue = {
      name: { label: '', value: '' },
      template: { label: '', value: '' },
      receivedDateTime: { label: '', value: '' },
      startDateTime: { label: '', value: '' },
      stopDateTime: { label: '', value: '' },
      timeZone: { label: '', value: '' },
      caseType: { label: '', value: '' },
      lea: { label: '', value: '' },
      caseOptTemplateUsed: { label: '', value: '' },
      contact: { label: '', value: '' },
      visibility: { label: '', value: '' },
      attachments: { label: '', value: [] },
    };
    return warrantFields.reduce((previous, fieldItem) => ({ ...previous, [fieldItem.field]: fieldItem }), initialValue);
  }

  renderWarrant = () => {

    const warrantFieldsMap = Summary.getWarrantFieldsMap(this.warrantFields);

    return (
      <div>
        <div className="summary-title">{i18n.t('warrant.summary.warrantInformation')}</div>
        <div id="summary" className="summary-card summary-warrant">
          <div className="summary-card-info">
            { this.renderSummaryField(warrantFieldsMap.name.label, this.toString(warrantFieldsMap.name.value)) }
            { this.renderSummaryField(warrantFieldsMap.template.label, this.toString(warrantFieldsMap.template.value)) }
            { this.isFieldVisibleInTemplate('receivedDateTime') && (
              this.renderSummaryField(
                warrantFieldsMap.receivedDateTime.label || i18n.t('warrant.summary.receiveTime'),
                Summary.toDateString(warrantFieldsMap.receivedDateTime.value),
              )
            )}
          </div>

          <div className="summary-card-info">
            {
              this.renderSummaryField(
                warrantFieldsMap.startDateTime.label || i18n.t('warrant.summary.startTime'),
                Summary.toDateString(warrantFieldsMap.startDateTime.value),
              )
            }
            {
              this.renderSummaryField(
                warrantFieldsMap.stopDateTime.label,
                Summary.toDateString(warrantFieldsMap.stopDateTime.value),
              )
            }
            { this.renderSummaryField(warrantFieldsMap.timeZone.label || i18n.t('warrant.summary.timeZone'), this.toString(warrantFieldsMap.timeZone.value)) }
          </div>
          <div className="summary-card-info">
            {this.renderSummaryField(
              warrantFieldsMap.contact.label,
              this.toString(warrantFieldsMap.contact.value),
            )}
            {this.renderSummaryField(
              warrantFieldsMap.visibility.label,
              this.toString(warrantFieldsMap.visibility.value),
            )}
            {this.renderSummaryField(
              warrantFieldsMap.lea.label,
              this.toString(warrantFieldsMap.lea.value),
            )}
          </div>
          <div className="summary-card-info">
            {this.renderSummaryField(
              warrantFieldsMap.caseType.label,
              this.toString(warrantFieldsMap.caseType.value),
            )}
            {this.renderSummaryField(
              warrantFieldsMap.caseOptTemplateUsed.label,
              this.toString(warrantFieldsMap.caseOptTemplateUsed.value),
            )}
            {this.isFieldVisibleInTemplate('attachmentsDetails') && (
              this.renderSummaryFieldEllipsis(
                warrantFieldsMap.attachments.label || i18n.t('warrant.summary.attachments'),
                this.toString(warrantFieldsMap.attachments.value.join(', ')),
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  renderCase = (aCase:any) => {
    if (!aCase) {
      return;
    }

    if (aCase.case.includeInWarrant === false) return;

    // render new case
    if (this.isNewCase(aCase)) {
      return this.renderNewCase(aCase);
    }

    // render existing case
    return (
      <div key={this.nextId()} className="summary-card">
        <tbody>
          { /* render case name first */ }
          { aCase.liIdField ? this.renderField(aCase.liIdField, 0) : this.renderField({ label: i18n.t('warrant.summary.caseName'), value: aCase.case.liId, original: null }, 0)}
          { /* render other case fields first */ }
          { aCase.fields.map((field:any, i:number) => this.renderField(field, i + 1)) }
        </tbody>
      </div>
    );
  }

  renderCases= () => {
    const caseIds:string[] = Object.keys(this.cases);
    if (caseIds && caseIds.length > 0) {
      return (
        <div>
          <div className="summary-title">{i18n.t('warrant.summary.cases')}</div>
          <div className="summary-cards">
            {caseIds.map((caseId:any, i:number) => this.renderCase(this.cases[caseId]))}
          </div>
        </div>
      );
    }
    return null;
  }

  renderDevices = () => {
    const deviceKeys:string[] = Object.keys(this.devices);
    return (
      deviceKeys.length > 0 ? (
        <div>
          <div className="summary-title">{i18n.t('warrant.summary.devices')}</div>
          <div className="summary-cards">
            { deviceKeys.map((key:any) => this.renderDeviceItem(this.devices[key])) }
          </div>
        </div>
      ) : null
    );
  }

  renderSummaryAdvertise = () => {
    return (
      <p className="summary-advertise">
        {i18n.t('warrant.summary.advertise')}
      </p>
    );
  }

  render() {
    return (
      <div style={{ width: '100%', marginTop: '5px' }}>
        { this.renderSummaryAdvertise()}
        { this.renderWarrant() }
        { this.renderDevices() }
        { this.renderCases() }
      </div>
    );
  }
}

const mapStateToProps = ({
  warrants: {
    warrantConfigInstance, warrant, templateUsed, customizationconfig, changeTracker,
  },
  cases: { cases },
  targets: { byId: targetsById },
  contacts: { contacts },
  users: { teams },
}: RootState) => ({
  warrantConfigInstance,
  templateUsed,
  warrant,
  changeTracker,
  derivedTargets: customizationconfig.derivedTargets,
  cases,
  contacts,
  teams,
  targetsById,
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(Summary);
